// quiron/frontend/src/components/ConsultaDefinitions.js

import React, { useState, useEffect, useCallback } from 'react'; // Adicionado useCallback
import { useTranslation } from 'react-i18next';
import { api, getAnamnesisTemplates as fetchUserTemplatesApi, /* outras importações de api se necessário */ } from '../api'; // Usar getAnamnesisTemplates da api.js
import { ANAMNESE_DATA } from '../anamneses';
import QuironTipTapEditor from './QuironTipTapEditor';
import { useNotification } from '../contexts/NotificationContext';
import styles from './Profile.module.css';

const ConsultaDefinitions = () => {
  const { t } = useTranslation();
  const { addNotification } = useNotification();

  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [selectedConsultationType, setSelectedConsultationType] = useState('');
  const [currentTemplateContent, setCurrentTemplateContent] = useState('');
  const [userTemplates, setUserTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  const specialties = Object.keys(ANAMNESE_DATA);
  // consultationTypes agora é um array fixo, pois a estrutura de ANAMNESE_DATA é { specialty: { first: "...", return: "..." } }
  const consultationTypesFixed = ['first', 'return'];


  const fetchUserTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);
    try {
      const data = await fetchUserTemplatesApi(); // Usando a função importada de api.js
      if (Array.isArray(data)) {
        setUserTemplates(data);
      } else {
        console.error('API de templates de anamnese não retornou um array:', data);
        setUserTemplates([]);
        addNotification(t('errorLoadingUserTemplates') + (data?.error ? `: ${data.error}` : ': Formato inválido'), 'error');
      }
    } catch (error) {
      console.error('Erro ao carregar templates de anamnese do usuário:', error);
      setUserTemplates([]); // Fallback para array vazio em caso de falha na chamada da API
      // A notificação de erro já é tratada dentro de getAnamnesisTemplates na api.js
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [addNotification, t]); // Dependências de useCallback

  useEffect(() => {
    fetchUserTemplates();
  }, [fetchUserTemplates]); // Executa ao montar e se fetchUserTemplates mudar (o que não deve acontecer)

  useEffect(() => {
    if (selectedSpecialty && selectedConsultationType) {
      setIsLoadingTemplates(true); // Indica que estamos carregando/processando o template
      const userCustomTemplate = userTemplates.find(
        (tmpl) => tmpl.specialty === selectedSpecialty && tmpl.consultation_type === selectedConsultationType
      );

      if (userCustomTemplate) {
        setCurrentTemplateContent(userCustomTemplate.content);
      } else {
        const defaultTemplate = ANAMNESE_DATA[selectedSpecialty]?.[selectedConsultationType];
        setCurrentTemplateContent(defaultTemplate || '');
      }
      setIsLoadingTemplates(false); // Finaliza o carregamento do template
    } else {
      setCurrentTemplateContent(''); // Limpa se não houver seleção completa
    }
  }, [selectedSpecialty, selectedConsultationType, userTemplates]);

  const handleSaveTemplate = async () => {
    if (!selectedSpecialty || !selectedConsultationType) { // Removida verificação de !currentTemplateContent para permitir salvar template vazio
      addNotification(t('fillAllTemplateFields'), 'error');
      return;
    }

    try {
      const payload = {
        specialty: selectedSpecialty,
        consultation_type: selectedConsultationType,
        content: currentTemplateContent, // Pode ser string vazia
      };
      // Usar api.post ou uma função específica se definida em api.js
      await api.post('/settings/anamnesis-templates', payload);
      
      // Atualizar a lista de templates do usuário após salvar
      fetchUserTemplates(); // Rebusca todos os templates

      addNotification(t('templateSavedSuccess'), 'success');
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      addNotification(t('errorSavingTemplate') + (error.response?.data?.error ? `: ${error.response.data.error}` : ''), 'error');
    }
  };

  const handleRestoreDefault = async () => {
    if (!selectedSpecialty || !selectedConsultationType) {
      addNotification(t('selectTemplateToRestore'), 'error');
      return;
    }

    try {
      // Deleta o template customizado do backend
      await api.delete(`/settings/anamnesis-templates/${selectedSpecialty}/${selectedConsultationType}`);
      
      // Atualiza o estado local removendo o template e recarregando o padrão
      setUserTemplates((prev) =>
        prev.filter(
          (tmpl) => !(tmpl.specialty === selectedSpecialty && tmpl.consultation_type === selectedConsultationType)
        )
      );
      
      // Carrega o conteúdo padrão no editor
      const defaultTemplate = ANAMNESE_DATA[selectedSpecialty]?.[selectedConsultationType];
      setCurrentTemplateContent(defaultTemplate || '');

      addNotification(t('templateRestoredSuccess'), 'success');
    } catch (error) {
      console.error('Erro ao restaurar template padrão:', error);
      addNotification(t('errorRestoringTemplate') + (error.response?.data?.error ? `: ${error.response.data.error}` : ''), 'error');
    }
  };
  
  // Handler para mudança de conteúdo no editor
  const handleContentChange = (content) => {
    setCurrentTemplateContent(content);
  };

  return (
    <div className={styles['settings-subsection']}>
      <h4>{t('anamnesisTemplates')}</h4>
      <div className={styles['template-selection']}>
        <select
          value={selectedSpecialty}
          onChange={(e) => {
            setSelectedSpecialty(e.target.value);
            setSelectedConsultationType(''); 
          }}
          className={styles['settings-input']} // Aplicando classe de estilo geral
        >
          <option value="">{t('selectSpecialty')}</option>
          {specialties.map((spec) => (
            <option key={spec} value={spec}>
              {t(spec)}
            </option>
          ))}
        </select>

        <select
          value={selectedConsultationType}
          onChange={(e) => setSelectedConsultationType(e.target.value)}
          disabled={!selectedSpecialty}
          className={styles['settings-input']} // Aplicando classe de estilo geral
        >
          <option value="">{t('selectConsultationType')}</option>
          {/* Usar consultationTypesFixed que é ['first', 'return'] */}
          {consultationTypesFixed.map((type) => (
            <option key={type} value={type}>
              {/* Ajustar a chave de tradução conforme necessário, ex: 'firstConsultation', 'returnConsultation' */}
              {t(type === 'first' ? 'firstConsultation' : 'returnConsultation')}
            </option>
          ))}
        </select>
      </div>

      {/* Renderiza o editor apenas se specialty e type estiverem selecionados e templates não estiverem carregando */}
      {selectedSpecialty && selectedConsultationType && !isLoadingTemplates && (
        <div className={styles['template-editor-container']}>
          <QuironTipTapEditor
            key={`template-editor-${selectedSpecialty}-${selectedConsultationType}`}
            value={currentTemplateContent} // Usar value em vez de content
            onChange={handleContentChange} // Usar onChange em vez de onContentChange
            autosavePrefix={`template_${selectedSpecialty}_${selectedConsultationType}`} // Chave de autosave mais específica
            specialty={selectedSpecialty}
            consultationType={selectedConsultationType}
            height={400} // Definir altura padrão
          />
          <div className={styles['template-actions']}>
            <button onClick={handleSaveTemplate} className={`${styles['button-base']} ${styles['button-primary']}`}>{t('saveTemplate')}</button>
            <button onClick={handleRestoreDefault} className={`${styles['button-base']} ${styles['button-neutral']}`}>{t('restoreDefault')}</button>
          </div>
        </div>
      )}
       {isLoadingTemplates && selectedSpecialty && selectedConsultationType && (
         <div className={styles.editorPlaceholder}>{t('loadingTemplateContent')}</div>
       )}
       {(!selectedSpecialty || !selectedConsultationType) && (
         <div className={styles.editorPlaceholder}>{t('selectSpecialtyAndTypeToEditTemplate')}</div>
       )}
    </div>
  );
};

export default ConsultaDefinitions;